import React, {useEffect, useRef, useState} from 'react';
import {Box} from '@mui/material';
import {Tooltip} from '@lightricks/react-design-system';
import extractBackendErrorDetail from '@/utils/extractBackendErrorDetail';
import {raiseFlashMessageError} from '@/utils/raiseFlashMessage';
import queryClient from '@/utils/reactQueryClient';
import translate from '@/utils/translate';
import {ENTER_KEY, ESCAPE_KEY} from '@/config/constants';
import type {BrandProfileNote} from '@/types/brandProfileNote';
import brandProfileNoteFetchers from '@/api/fetchers/brandProfileNote';
import type {CampaignCreatorRow} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import useUpdateBrandProfileNote from '@/hooks/mutations/use-update-brand-profile-note';
import useBrandProfileNoteQuery from '@/hooks/queries/use-brand-profile-note-query';
import useBrandId from '@/hooks/use-brand-id';
import styles from './NotesCell.module.scss';

interface NotesCellProps {
  creator: CampaignCreatorRow;
  onActionComplete: () => void;
}

function NotesCell({creator, onActionComplete}: NotesCellProps) {
  const brandId = useBrandId();
  const [isEditing, setIsEditing] = useState(false);
  const [displayNote, setDisplayNote] = useState<string | null>(
    creator.brandNote
  );
  const [inputValue, setInputValue] = useState(creator.brandNote ?? '');
  const inputRef = useRef<HTMLInputElement>(null);
  const skipBlurSaveRef = useRef(false);
  const saveInFlightRef = useRef(false);

  const {data: note} = useBrandProfileNoteQuery({
    brandId: brandId ?? '',
    profileId: creator.profileId,
    enabled: Boolean(brandId) && isEditing,
  });

  const {mutateAsync: updateNote, isLoading: isSaving} =
    useUpdateBrandProfileNote({
      brandId: brandId ?? '',
      profileId: creator.profileId,
    });

  const placeholder = translate('Add note');

  useEffect(() => {
    setDisplayNote(creator.brandNote);
    setInputValue(creator.brandNote ?? '');
    setIsEditing(false);
  }, [creator.brandNote, creator.id]);

  const resolveNote = async (): Promise<BrandProfileNote> => {
    if (note) {
      return note;
    }

    // fetchQuery returns the raw JSON:API payload; useDataQuery normally
    // flattens this so callers get BrandProfileNote via `.data`.
    const result = (await queryClient.fetchQuery({
      queryKey: ['brandProfileNote', brandId, creator.profileId],
      queryFn: brandProfileNoteFetchers.note,
      meta: {brandId, profileId: creator.profileId},
    })) as {data: BrandProfileNote};
    return result.data;
  };

  const handleSave = async () => {
    if (!brandId || isSaving || saveInFlightRef.current) {
      return;
    }

    const nextValue = inputValue;
    if (nextValue === (displayNote ?? '')) {
      setIsEditing(false);
      return;
    }

    saveInFlightRef.current = true;
    try {
      const noteData = await resolveNote();
      await updateNote({...noteData, bodyRaw: nextValue});
      setDisplayNote(nextValue || null);
      setIsEditing(false);
      onActionComplete();
    } catch (error) {
      raiseFlashMessageError({
        subtitle: extractBackendErrorDetail(error),
      });
    } finally {
      saveInFlightRef.current = false;
    }
  };

  const handleFocus = () => {
    if (!brandId || isSaving) {
      return;
    }
    setIsEditing(true);
    if (displayNote) {
      requestAnimationFrame(() => inputRef.current?.select());
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === ENTER_KEY.key) {
      event.preventDefault();
      inputRef.current?.blur();
      return;
    }

    if (event.key === ESCAPE_KEY.key) {
      event.preventDefault();
      skipBlurSaveRef.current = true;
      setInputValue(displayNote ?? '');
      setIsEditing(false);
      inputRef.current?.blur();
    }
  };

  const handleBlur = () => {
    if (skipBlurSaveRef.current) {
      skipBlurSaveRef.current = false;
      return;
    }
    handleSave().catch(() => {
      // Errors are surfaced inside handleSave via flash message.
    });
  };

  const stopRowClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <div
      className={styles.container}
      onClick={stopRowClick}
      onMouseDown={stopRowClick}
    >
      <Tooltip
        title={displayNote && !isEditing ? displayNote : undefined}
        arrow
        placement="top"
      >
        <Box component="span" className={styles.inputTooltipTrigger}>
          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            value={inputValue}
            placeholder={placeholder}
            disabled={!brandId || isSaving}
            onFocus={handleFocus}
            onChange={(event) => setInputValue(event.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            aria-label={translate('Profile Notes')}
          />
        </Box>
      </Tooltip>
    </div>
  );
}

export default NotesCell;
